"use client";

import { useState, useEffect, useCallback, Suspense } from "react";
import { Flame } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import Link from "next/link";

function fmtCents(price: number): string {
  const cents = (price ?? 0) * 100;
  const rounded = Math.round(cents);
  return Math.abs(cents - rounded) > 0.05 ? cents.toFixed(1) : rounded.toString();
}
import MarketSparkline from "./MarketSparkline";
import { trackRecommendation, setNavigationSource } from "@/lib/recommendation-tracker";
import { sourceUrl } from "@/lib/source-url";
import { useCurrentSource } from "@/hooks/useCurrentSource";
import { useTrendingMarketsQuery, type TrendingMarket } from "@/hooks/useTrendingMarketsQuery";
import { useQueryClient } from "@tanstack/react-query";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { fetchEventData } from "@/hooks/useEventQuery";

function formatVolume(volume: number): string {
  const v = Number(volume) || 0;
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

function getYesPrice(market: TrendingMarket): number | null {
  const raw = market.markets?.[0]?.outcomePrices || market.outcomePrices;
  if (!raw) return null;
  try {
    const prices = typeof raw === "string" ? JSON.parse(raw) : raw;
    return prices?.[0] ? parseFloat(prices[0]) : null;
  } catch {
    return null;
  }
}

function getYesTokenId(market: TrendingMarket): string | null {
  // Try nested market first (events), then top-level
  const sources = [
    market.markets?.[0]?.clobTokenIds,
    market.clobTokenIds,
  ];
  for (const raw of sources) {
    if (!raw) continue;
    try {
      const ids = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (Array.isArray(ids) && ids[0]) return ids[0];
    } catch { /* skip */ }
  }
  return null;
}


function TrendingMarketsContent() {
  const t = useT();
  const source = useCurrentSource();
  const { data: markets = [], isLoading: loading } = useTrendingMarketsQuery();
  const queryClient = useQueryClient();
  // Must match useEventQuery's locale-aware key (["event-page", source,
  // slug, locale]); without locale the prefetch warmed a dead 3-element
  // key the live query never read, defeating hover-prewarm. market-page's
  // live key has no locale, so that branch stays as-is.
  const locale = useLocale();

  const prefetchMarketData = useCallback((market: TrendingMarket) => {
    const isEvent = market.markets && market.markets.length > 1;
    if (isEvent) {
      queryClient.prefetchQuery({
        queryKey: ["event-page", "polymarket", market.slug, locale],
        // Reuse the live query's fetcher so the warmed payload shape is
        // byte-identical to what the page reads (no cache poisoning).
        queryFn: () => fetchEventData(market.slug, "polymarket"),
        staleTime: 30_000,
      });
    } else {
      queryClient.prefetchQuery({
        queryKey: ["market-page", "polymarket", market.slug],
        queryFn: async () => {
          const res = await fetch(`/api/polymarket/market/${market.slug}`);
          if (!res.ok) return null;
          return { market: await res.json(), umaDispute: null };
        },
        staleTime: 30_000,
      });
    }
  }, [queryClient, locale]);

  const cardClasses = "bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-5";

  if (loading) {
    return (
      <div className={cardClasses}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-satoshi font-bold text-gray-900 dark:text-white text-sm flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />{t("sidebar.trending.title")}</h3>
          <span className="text-[10px] font-satoshi text-gray-400 dark:text-gray-500">{t("sidebar.trending.volume")}</span>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse flex gap-3 items-center">
              <div className="w-10 h-10 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="h-3 bg-gray-100 dark:bg-[#0f0f12] rounded w-full mb-2" />
                <div className="h-2.5 bg-gray-100 dark:bg-[#0f0f12] rounded w-1/3" />
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="w-[72px] h-[28px] bg-gray-100 dark:bg-[#0f0f12] rounded" />
                <div className="h-3 bg-gray-100 dark:bg-[#0f0f12] rounded w-10" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (markets.length === 0) {
    return (
      <div className={cardClasses}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-satoshi font-bold text-gray-900 dark:text-white text-sm flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />{t("sidebar.trending.title")}</h3>
        </div>
        <p className="text-xs font-satoshi text-gray-500 dark:text-gray-400 text-center py-6">
          {t("sidebar.trending.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className={cardClasses}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-satoshi font-bold text-gray-900 dark:text-white text-sm flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />{t("sidebar.trending.title")}</h3>
        <span className="text-[10px] font-satoshi text-gray-400 dark:text-gray-500">{t("sidebar.trending.volume")}</span>
      </div>
      <div className="space-y-1">
        {markets.slice(0, 5).map((market) => {
          const isEvent = market.markets && market.markets.length > 1;
          const href = isEvent
            ? sourceUrl(`/event/${market.slug}`, "polymarket")
            : sourceUrl(`/market/${market.slug}`, "polymarket");
          const yesPrice = getYesPrice(market);
          const priceChange = market.oneDayPriceChange;
          const tokenId = getYesTokenId(market);
          const isUp = priceChange != null ? priceChange >= 0 : true;

          return (
            <Link
              key={market.id}
              href={href}
              prefetch={false}
              onMouseEnter={() => prefetchMarketData(market)}
              onClick={() => { trackRecommendation(market.slug, 'view_detail', 'trending'); setNavigationSource('trending'); }}
              className="block group"
            >
              <div className="flex gap-2.5 items-center hover:bg-gray-50/80 dark:hover:bg-white/[0.04] p-2.5 -mx-2.5 rounded-[5px] transition-colors">
                {/* Thumbnail */}
                <div className="flex-shrink-0 relative">
                  {market.image ? (
                    <div className="w-10 h-10 rounded-[5px] border border-gray-200/40 dark:border-[#1A1A1A] overflow-hidden">
                      <CdnImage
                        src={market.image}
                        alt={market.title}
                        width={40}
                        height={40}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] flex items-center justify-center">
                      <span className="text-xs font-bold text-gray-400">?</span>
                    </div>
                  )}
                  {/* Polymarket badge */}
                  <div className="absolute -bottom-0.5 -right-0.5 w-[14px] h-[14px] rounded-[3px] bg-[#2E5CFF] flex items-center justify-center ring-[1.5px] ring-white dark:ring-[#0f0f12]">
                    <CdnImage src="/logos/polymarket/icon-white.png" alt="" width={8} height={8} className="w-2 h-2 object-contain" />
                  </div>
                </div>

                {/* Title + Volume */}
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-satoshi font-medium text-gray-900 dark:text-white line-clamp-1 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors leading-snug">
                    {market.title}
                  </p>
                  <span className="text-[10px] font-satoshi text-gray-400 dark:text-gray-500 mt-0.5 block">
                    {t("sidebar.trending.vol", { v: formatVolume(market.volume24hr) })}
                  </span>
                </div>

                {/* Sparkline + Price + Change */}
                <div className="flex-shrink-0 flex flex-col items-end gap-1">
                  {tokenId ? (
                    <MarketSparkline tokenId={tokenId} width={72} height={28} />
                  ) : (
                    <div className="w-[72px] h-[28px]" />
                  )}
                  <div className="flex items-center gap-1">
                    {yesPrice != null && (
                      <span className="text-[11px] font-satoshi font-bold text-gray-900 dark:text-white tabular-nums">
                        {fmtCents(yesPrice)}¢
                      </span>
                    )}
                    {priceChange != null && priceChange !== 0 && (
                      <span className={`text-[10px] font-bold tabular-nums px-1 py-px rounded ${
                        isUp
                          ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10"
                          : "text-red-600 dark:text-red-400 bg-red-500/10"
                      }`}>
                        {isUp ? "+" : ""}{priceChange.toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// Wrap in Suspense for SSR compatibility (useCurrentSource → useSearchParams requires it)
export default function TrendingMarkets() {
  return (
    <Suspense fallback={null}>
      <TrendingMarketsContent />
    </Suspense>
  );
}
